import { Dispatch, FC, SetStateAction, useMemo, useRef, useState } from "react";
import { StatusCodeFilter } from "../map-asset/statusCodeFilter";
import {
  RouteRenderMode,
  TimedPathPoint,
} from "../map-asset/multiDay/resolveRoutePath";
import { useResolvedRoutePaths } from "./useResolvedRoutePaths";
import { useTranslation } from "react-i18next";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../api/types/VehicleStatusCode";
import svgIcons from "../../assets/svg/svgIcons";
import { compareColorForSlot } from "../map-asset/multiDay/tripCompareColors";
import {
  classifyTrip,
  TRIP_CLASSIFICATION_LABEL_KEY,
} from "../map-asset/multiDay/tripClassification";
import { TripMiniMap } from "../map-asset/multiDay/TripMiniMap";
import {
  tripDisplayNumberOr,
  tripDisplayNumbers,
} from "../map-asset/multiDay/tripNumbering";
import { CompareEventFilter } from "../map-asset/multiDay/CompareEventFilter";
import {
  DetailMapTrip,
  TripDetailMap,
} from "../map-asset/multiDay/TripDetailMap";
import { VehicleGroup } from "./useMultiVehicleTripData";

type Props = {
  vehicleGroups: VehicleGroup[];
  tripKeys: string[];
  timezone: string;
  // an account can rename its status codes - passed through so the Event
  // Filter here reads the same as the one on the map behind it
  statusCodeDescriptions?: Record<number, string>;
  // what the filter starts on. carries the map's current filter in, which is
  // also how the account's "Hide Asset Map Pinpoints" reaches this view -
  // without it, compare would cheerfully pin every event on an account that
  // has asked for none
  initialFilteredStatusCodes?: StatusCodeFilter;
  // the Route View the map is on - compare follows it rather than
  // keeping its own, so the same trip can't be road-aligned on one
  // screen and raw fixes on the other
  routeMode?: RouteRenderMode;
  onBack: () => void;
  onManageSelection: () => void;
  setSelectedTripPoint: Dispatch<SetStateAction<VehicleMapDataPoint | null>>;
};

// replaces the map while comparing. one card per asset, holding every trip
// picked for that asset - so comparing a whole day against another vehicle's
// day puts each day on its own map rather than scattering it across cards.
export const VehicleCompareView: FC<Props> = ({
  vehicleGroups,
  tripKeys,
  timezone,
  statusCodeDescriptions,
  initialFilteredStatusCodes,
  routeMode = "DEFAULT",
  onBack,
  onManageSelection,
  setSelectedTripPoint,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.tripDetails",
  });
  const [showEvents, setShowEvents] = useState(false);
  // which event types get a pin here; null means the filter hasn't been
  // applied so all of them are pinned, and an empty array means none are.
  // Owned at this level so the cards and the expanded map can't disagree
  // about what is pinned.
  const [filteredStatusCodes, setFilteredStatusCodes] =
    useState<StatusCodeFilter>(() => initialFilteredStatusCodes ?? null);

  // flat list for the resolver, keyed exactly as the map keys them so the
  // cache lines up
  const tripsToResolve = useMemo(() => {
    const trips: {
      tripKey: string;
      tripIndex: number;
      points: VehicleMapDataPoint[];
    }[] = [];
    tripKeys.forEach((tripKey) => {
      const [deviceId, tripIndexKey] = tripKey.split("__");
      const group = vehicleGroups.find((item) => item.deviceId === deviceId);
      const tripIndex = Number(tripIndexKey);
      const points = group?.trips[tripIndex];
      if (!points || points.length < 2) return;
      trips.push({ tripKey, tripIndex, points });
    });
    return trips;
  }, [tripKeys, vehicleGroups]);

  // Compare used to draw raw fixes while the map behind it drew road-aligned
  // routes, so the same trip visibly jumped off the road on entering this
  // screen. Resolving here closes that: the paths the map already resolved
  // come straight back out of resolveRoutePath's cache, so in the common
  // case this costs nothing.
  const resolvedPathsRef = useRef<Map<string, TimedPathPoint[]>>(new Map());
  // cards render from a memo, so a ref alone would never repaint them
  const [resolvedVersion, setResolvedVersion] = useState(0);

  useResolvedRoutePaths({
    trips: tripsToResolve,
    mode: routeMode,
    pathsRef: resolvedPathsRef,
    onResolved: () => setResolvedVersion((version) => version + 1),
  });

  const cards = useMemo(() => {
    const byDevice = new Map<
      string,
      {
        deviceId: string;
        vehicleLabel: string;
        vehicleFullLabel: string;
        vehicleColor: string;
        trips: {
          tripKey: string;
          tripNumber: number;
          classificationLabel: string;
          points: (typeof vehicleGroups)[number]["trips"][number];
          path: { lat: number; lng: number }[];
        }[];
      }
    >();

    tripKeys.forEach((tripKey) => {
      const [deviceId, tripIndexKey] = tripKey.split("__");
      const group = vehicleGroups.find((item) => item.deviceId === deviceId);
      const tripIndex = Number(tripIndexKey);
      const points = group?.trips[tripIndex];
      if (!group || !points || points.length === 0) return;

      const card = byDevice.get(deviceId) ?? {
        deviceId,
        vehicleLabel: group.label,
        vehicleFullLabel: group.fullLabel,
        vehicleColor: group.color,
        trips: [],
      };
      card.trips.push({
        tripKey,
        tripNumber: tripDisplayNumberOr(
          tripDisplayNumbers(group.trips),
          tripIndex
        ),
        classificationLabel: t(
          TRIP_CLASSIFICATION_LABEL_KEY[classifyTrip(points)]
        ),
        points,
        // the road-aligned path once it has resolved, raw fixes until then
        path:
          resolvedPathsRef.current
            .get(tripKey)
            ?.map((point) => ({ lat: point.lat, lng: point.lng })) ??
          points.map((point) => ({
            lat: point.latitude,
            lng: point.longitude,
          })),
      });
      byDevice.set(deviceId, card);
    });

    return Array.from(byDevice.values()).map((card) => ({
      ...card,
      trips: card.trips.sort((a, b) => a.tripNumber - b.tripNumber),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripKeys, vehicleGroups, t, resolvedVersion]);

  const totalTrips = cards.reduce((sum, card) => sum + card.trips.length, 0);

  // a card with one trip keeps the vehicle's own colour, so it still reads
  // as the same route that was on the map. several trips on one card need
  // telling apart, so they take the per-slot palette instead.
  const colorForTrip = (
    card: (typeof cards)[number],
    tripSlotIndex: number
  ) =>
    card.trips.length === 1
      ? card.vehicleColor
      : compareColorForSlot(tripSlotIndex);

  // the Event Filter's options - whatever event types actually turn up
  // across the trips being compared, same as the main maps derive theirs
  const filterableStatusCodes = useMemo(() => {
    const codes = new Set<VehicleStatusCode>();
    cards.forEach((card) =>
      card.trips.forEach((trip) =>
        trip.points.forEach((point) => codes.add(point.statuscode))
      )
    );
    return Array.from(codes);
  }, [cards]);

  // the expanded map is interactive - hover, click, a legend - so it
  // doesn't need the mini-map's per-slot palette trick to tell same-vehicle
  // trips apart. every trip just takes its own vehicle's real colour.
  const detailMapTripsFor = (card: (typeof cards)[number]): DetailMapTrip[] =>
    card.trips.map((trip) => ({
      groupId: card.deviceId,
      groupLabel: card.vehicleLabel,
      groupFullLabel: card.vehicleFullLabel,
      tripNumber: trip.tripNumber,
      color: card.vehicleColor,
      points: trip.points,
    }));

  // the full-detail expanded map - one card, or several checked ones
  // overlapped together. null collapses back to the card grid.
  const [detailView, setDetailView] = useState<{
    title: string;
    trips: DetailMapTrip[];
  } | null>(null);

  // which cards are checked for "View Together" - separate from picking
  // which trips are in Compare at all (that's Manage Trips Selection)
  const [checkedDeviceIds, setCheckedDeviceIds] = useState<Set<string>>(
    new Set()
  );
  const toggleChecked = (deviceId: string) =>
    setCheckedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });

  const checkedCards = cards.filter((card) =>
    checkedDeviceIds.has(card.deviceId)
  );

  if (detailView) {
    return (
      <TripDetailMap
        title={detailView.title}
        trips={detailView.trips}
        timezone={timezone}
        filteredStatusCodes={filteredStatusCodes}
        resolvedPathsRef={resolvedPathsRef}
        routeMode={routeMode}
        onClose={() => setDetailView(null)}
        onTripPointClick={setSelectedTripPoint}
      />
    );
  }

  return (
    <div className="absolute inset-0 z-[80] bg-white flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-accent-blue-bright font-semibold text-base"
        >
          <img src={svgIcons.ArrowLeftIcon} alt="" className="w-4 h-4" />
          Comparing {totalTrips} Trip{totalTrips === 1 ? "" : "s"}
          {cards.length > 1 ? ` across ${cards.length} Vehicles` : ""}
        </button>
        <div className="flex items-center gap-4 shrink-0">
          {checkedCards.length >= 2 && (
            <button
              type="button"
              onClick={() =>
                setDetailView({
                  title: `${checkedCards.length} Vehicles Together`,
                  trips: checkedCards.flatMap(detailMapTripsFor),
                })
              }
              className="px-3 py-1.5 rounded-lg bg-accent-blue-pale text-accent-blue-dark text-sm font-medium hover:bg-accent-blue-paleO66"
            >
              View {checkedCards.length} Together
            </button>
          )}
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              className="size-4 accent-accent-blue-dark cursor-pointer"
              checked={showEvents}
              onChange={(e) => setShowEvents(e.target.checked)}
            />
            Show events
          </label>
          <CompareEventFilter
            statusCodes={filterableStatusCodes}
            filteredStatusCodes={filteredStatusCodes}
            onChange={setFilteredStatusCodes}
            statusCodeDescriptions={statusCodeDescriptions}
          />
          <button
            type="button"
            onClick={onManageSelection}
            className="px-4 py-1.5 rounded-lg border border-accent-blue-bright text-accent-blue-bright text-sm font-medium hover:bg-accent-blue-paleO66"
          >
            Manage Trips Selection
          </button>
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="flex-grow flex items-center justify-center text-heading-gray888 text-sm">
          No trips selected to compare.
        </div>
      ) : (
        <div className="flex-grow flex gap-4 p-4 overflow-x-auto">
          {cards.map((card) => (
            <div
              key={card.deviceId}
              className="flex-1 min-w-[260px] flex flex-col border border-gray-200 rounded-lg overflow-hidden"
            >
              <div className="px-3 py-2 border-b border-gray-200 shrink-0">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    className="size-3.5 accent-accent-blue-dark cursor-pointer shrink-0"
                    checked={checkedDeviceIds.has(card.deviceId)}
                    onChange={() => toggleChecked(card.deviceId)}
                    aria-label={`Select ${card.vehicleFullLabel} to view together`}
                  />
                  <span
                    className="inline-block size-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: card.vehicleColor }}
                  />
                  <span className="truncate">{card.vehicleFullLabel}</span>
                  <span className="text-heading-gray888 font-normal shrink-0">
                    ({card.trips.length} Trip
                    {card.trips.length === 1 ? "" : "s"})
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setDetailView({
                        title: `${card.vehicleFullLabel} · ${card.trips.length} Trip${card.trips.length === 1 ? "" : "s"}`,
                        trips: detailMapTripsFor(card),
                      })
                    }
                    title="Expand"
                    aria-label={`Expand ${card.vehicleFullLabel}`}
                    className="ms-auto size-6 shrink-0 flex items-center justify-center rounded-lg hover:bg-gray-100"
                  >
                    <img
                      src={svgIcons.ExpandWindowIcon}
                      alt=""
                      className="size-4"
                    />
                  </button>
                </div>

                {/* legend - the only way to tell one trip from another once
                    several share a map */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                  {card.trips.map((trip, tripSlotIndex) => (
                    <span
                      key={trip.tripKey}
                      className="flex items-center gap-1.5 text-xs text-heading-gray888"
                    >
                      <span
                        className="inline-block w-3 h-1 rounded-full shrink-0"
                        style={{
                          backgroundColor: colorForTrip(card, tripSlotIndex),
                        }}
                      />
                      Trip {trip.tripNumber}
                      {card.trips.length === 1
                        ? ` (${trip.classificationLabel})`
                        : ""}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-grow min-h-[200px]">
                <TripMiniMap
                  routes={card.trips.map((trip, tripSlotIndex) => ({
                    path: trip.path,
                    color: colorForTrip(card, tripSlotIndex),
                    points: trip.points,
                  }))}
                  showEvents={showEvents}
                  filteredStatusCodes={filteredStatusCodes}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
